import React, { useState, useMemo, useEffect } from 'react';
import { AlertTriangle, X, Clock, CreditCard } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';

const AUTO_DISMISS_MS = 10000;
const SEEN_KEY = 'hmc_trial_banner_seen';

const TrialWarningBanner = () => {
  const { user, institution } = useAuth();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(SEEN_KEY) === '1');

  const daysRemaining = useMemo(() => {
    if (institution?.subscription_status === 'trial' && institution?.trial_end_date) {
      const diffTime = new Date(institution.trial_end_date) - new Date();
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return null;
  }, [institution]);

  const visible = !!institution && institution.subscription_status === 'trial' && !dismissed && daysRemaining !== null && daysRemaining <= 7;

  const dismiss = () => {
    sessionStorage.setItem(SEEN_KEY, '1');
    setDismissed(true);
  };

  // One-time popup: auto-hides after 10s so it never keeps covering the page header
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  if (!visible) return null;

  const isExpired = daysRemaining <= 0;
  const isUrgent = daysRemaining <= 3;

  return (
    <div
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] w-[min(92vw,640px)] rounded-xl overflow-hidden shadow-2xl text-white animate-in slide-in-from-top-4 fade-in duration-300
        ${isExpired ? 'bg-red-600' : isUrgent ? 'bg-amber-500' : 'bg-amber-400'}`}
      role="status"
      data-testid="trial-warning-banner"
    >
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-3">
          {isExpired ? <AlertTriangle className="w-5 h-5 flex-shrink-0" /> : <Clock className="w-5 h-5 flex-shrink-0" />}
          <div className="text-sm font-medium">
            {isExpired ? (
              <span>Your trial has expired. Subscribe now to continue using HMC Buddy.</span>
            ) : daysRemaining === 1 ? (
              <span>Your trial expires <strong>tomorrow</strong>! Subscribe to avoid service interruption.</span>
            ) : (
              <span>Your trial expires in <strong>{daysRemaining} days</strong>. Subscribe to continue uninterrupted access.</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {user?.is_master_admin && (
            <Link
              to="/settings/institution"
              onClick={dismiss}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                ${isExpired ? 'bg-white text-red-600 hover:bg-red-50' : 'bg-white/20 hover:bg-white/30'}`}
              data-testid="subscribe-btn"
            >
              <CreditCard className="w-4 h-4" />
              <span>Subscribe Now</span>
            </Link>
          )}
          <button
            onClick={dismiss}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss"
            data-testid="dismiss-trial-banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="h-1 bg-white/25">
        <div
          className="h-full bg-white/80"
          style={{ animation: `trial-banner-countdown ${AUTO_DISMISS_MS}ms linear forwards` }}
          data-testid="trial-banner-countdown"
        />
      </div>
    </div>
  );
};

export default TrialWarningBanner;
